import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import UnifiedTagDisplay from './UnifiedTagDisplay';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Menu,
  MenuItem,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  CreateNewFolder as CreateFolderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as UploadIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  Description as DocumentIcon
} from '@mui/icons-material';

interface Folder {
  id: string;
  name: string;
  parentFolderId: string;
  description: string;
  type: 'folder';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  isPublic: boolean;
  fileCount: number;
  folderCount: number;
  path: string;
  children?: Folder[];
}

interface FileInfo {
  id: string;
  name: string;
  uuidName: string;
  originalName: string;
  url: string;
  thumbnailUrl: string;
  size: number;
  sizeFormatted: string;
  contentType: string;
  fileType: string;
  category: string;
  icon: string;
  folderId: string;
  sourceFolderId: string;
  timeCreated: string;
  timeCreatedFormatted: string;
  updated: string;
  updatedFormatted: string;
  tags: string[];
  description: string;
  isPublic: boolean;
  canPlay: boolean;
  canPreview: boolean;
}

interface FolderManagerProps {
  onFileSelect?: (file: FileInfo) => void;
  onFolderSelect?: (folder: Folder) => void;
}

const FolderManager: React.FC<FolderManagerProps> = ({
  onFileSelect,
  onFolderSelect
}) => {
  // 狀態管理
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folderTree, setFolderTree] = useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
  const [folderContents, setFolderContents] = useState<FileInfo[]>([]);
  const [breadcrumbPath, setBreadcrumbPath] = useState<Folder[]>([]);
  
  // 對話框狀態
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [editFolderDialog, setEditFolderDialog] = useState(false);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [moveFileDialog, setMoveFileDialog] = useState(false);
  // 🔧 新增：檔案刪除對話框狀態
  const [deleteFileDialog, setDeleteFileDialog] = useState(false);
  
  // 表單狀態
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderDescription, setEditFolderDescription] = useState('');
  const [selectedFolderForEdit, setSelectedFolderForEdit] = useState<Folder | null>(null);
  const [selectedFolderForDelete, setSelectedFolderForDelete] = useState<Folder | null>(null);
  const [selectedFileForMove, setSelectedFileForMove] = useState<FileInfo | null>(null);
  // 🔧 新增：選中要刪除的檔案
  const [selectedFileForDelete, setSelectedFileForDelete] = useState<FileInfo | null>(null);
  const [targetFolderId, setTargetFolderId] = useState('root');
  
  // 載入狀態
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingContents, setLoadingContents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 菜單狀態
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedItemForMenu, setSelectedItemForMenu] = useState<Folder | FileInfo | null>(null);

  // 獲取資料夾結構
  const fetchFolders = async () => {
    setLoadingFolders(true);
    setError(null);
    
    try {
      const data = await apiService.get('/api/folders');
      if (data.success) {
        setFolders(data.folders);
        setFolderTree(data.folderTree);
      } else {
        throw new Error(data.error || '獲取資料夾結構失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '獲取資料夾結構失敗';
      setError(errorMessage);
      console.error('獲取資料夾結構錯誤:', err);
    } finally {
      setLoadingFolders(false);
    }
  };

  // 獲取資料夾內容
  const fetchFolderContents = async (folderId: string) => {
    setLoadingContents(true);
    setError(null);
    
    try {
      const data = await apiService.get(`/api/folder/${folderId}/contents`);
      if (data.success) {
        setFolderContents(data.files);
      } else {
        throw new Error(data.error || '獲取資料夾內容失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '獲取資料夾內容失敗';
      setError(errorMessage);
      console.error('獲取資料夾內容錯誤:', err);
    } finally {
      setLoadingContents(false);
    }
  };

  // 創建資料夾
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const data = await apiService.post('/api/create-folder', {
        folderName: newFolderName.trim(),
        parentFolderId: currentFolder?.id || 'root',
        description: newFolderDescription.trim()
      });

      if (data.success) {
        // 重新獲取資料夾結構
        await fetchFolders();
        setCreateFolderDialog(false);
        setNewFolderName('');
        setNewFolderDescription('');
      } else {
        throw new Error(data.error || '創建資料夾失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '創建資料夾失敗';
      setError(errorMessage);
      console.error('創建資料夾錯誤:', err);
    }
  };

  // 重命名資料夾
  const handleRenameFolder = async () => {
    if (!selectedFolderForEdit || !editFolderName.trim()) return;
    
    try {
      const data = await apiService.put('/api/rename-folder', {
        folderId: selectedFolderForEdit.id,
        newName: editFolderName.trim()
      });

      if (data.success) {
        // 重新獲取資料夾結構
        await fetchFolders();
        setEditFolderDialog(false);
        setSelectedFolderForEdit(null);
        setEditFolderName('');
      } else {
        throw new Error(data.error || '重命名資料夾失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '重命名資料夾失敗';
      setError(errorMessage);
      console.error('重命名資料夾錯誤:', err);
    }
  };

  // 刪除資料夾
  const handleDeleteFolder = async () => {
    if (!selectedFolderForDelete) return;
    
    try {
      const data = await apiService.delete('/api/delete-folder', {
        folderId: selectedFolderForDelete.id
      });

      if (data.success) {
        // 重新獲取資料夾結構
        await fetchFolders();
        setDeleteFolderDialog(false);
        setSelectedFolderForDelete(null);
        
        // 如果刪除的是當前資料夾，回到根目錄
        if (currentFolder?.id === selectedFolderForEdit?.id) {
          setCurrentFolder(null);
          setBreadcrumbPath([]);
          setFolderContents([]);
        }
      } else {
        throw new Error(data.error || '刪除資料夾失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '刪除資料夾失敗';
      setError(errorMessage);
      console.error('刪除資料夾錯誤:', err);
    }
  };

  // 移動檔案
  const handleMoveFile = async () => {
    if (!selectedFileForMove || !targetFolderId) return;
    
    try {
      const data = await apiService.put('/api/move-file', {
        filename: selectedFileForMove.uuidName,
        targetFolderId: targetFolderId,
        sourceFolderId: selectedFileForMove.folderId
      });

      if (data.success) {
        // 重新獲取資料夾內容
        if (currentFolder) {
          await fetchFolderContents(currentFolder.id);
        }
        setMoveFileDialog(false);
        setSelectedFileForMove(null);
        setTargetFolderId('root');
      } else {
        throw new Error(data.error || '移動檔案失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '移動檔案失敗';
      setError(errorMessage);
      console.error('移動檔案錯誤:', err);
    }
  };

  // 🔧 新增：刪除檔案
  const handleDeleteFile = async () => {
    if (!selectedFileForDelete) return;
    
    try {
      const data = await apiService.delete('/api/delete-file', {
        filename: selectedFileForDelete.uuidName
      });

      if (data.success) {
        // 重新獲取資料夾內容
        if (currentFolder) {
          await fetchFolderContents(currentFolder.id);
        } else {
          await fetchFolderContents('root');
        }
        setDeleteFileDialog(false);
        setSelectedFileForDelete(null);
      } else {
        throw new Error(data.error || '刪除檔案失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '刪除檔案失敗';
      setError(errorMessage);
      console.error('刪除檔案錯誤:', err);
    }
  };

  // 進入資料夾
  const enterFolder = (folder: Folder) => {
    setCurrentFolder(folder);
    
    // 更新麵包屑路徑
    const newPath = [...breadcrumbPath];
    if (folder.parentFolderId === 'root') {
      newPath.length = 0;
    } else {
      // 找到父資料夾在路徑中的位置
      const parentIndex = newPath.findIndex(f => f.id === folder.parentFolderId);
      if (parentIndex !== -1) {
        newPath.length = parentIndex + 1;
      }
    }
    newPath.push(folder);
    setBreadcrumbPath(newPath);
    
    // 獲取資料夾內容
    fetchFolderContents(folder.id);
    
    // 觸發回調
    onFolderSelect?.(folder);
  };

  // 回到根目錄
  const goToRoot = () => {
    setCurrentFolder(null);
    setBreadcrumbPath([]);
    setFolderContents([]);
    // 🔧 新增：回到根目錄時重新獲取根目錄內容
    fetchFolderContents('root');
  };

  // 回到上級資料夾
  const goToParent = () => {
    if (breadcrumbPath.length > 1) {
      const parentFolder = breadcrumbPath[breadcrumbPath.length - 2];
      enterFolder(parentFolder);
    } else {
      goToRoot();
    }
  };

  // 獲取檔案圖標
  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'video':
        return <VideoIcon />;
      case 'image':
        return <ImageIcon />;
      case 'audio':
        return <AudioIcon />;
      case 'document':
        return <DocumentIcon />;
      default:
        return <FileIcon />;
    }
  };

  // 格式化檔案大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 組件加載時獲取資料夾結構和根目錄內容
  useEffect(() => {
    fetchFolders();
    // 🔧 新增：同時獲取根目錄內容
    fetchFolderContents('root');
  }, []);

  // 渲染資料夾樹狀結構
  const renderFolderTree = (folderList: Folder[], level = 0) => {
    return folderList.map(folder => (
      <Box key={folder.id}>
        <ListItem
          disablePadding
          sx={{ pl: level * 2 }}
        >
          <ListItemButton
            onClick={() => enterFolder(folder)}
            selected={currentFolder?.id === folder.id}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                }
              }
            }}
          >
            <ListItemIcon>
              {currentFolder?.id === folder.id ? <FolderOpenIcon color="primary" /> : <FolderIcon />}
            </ListItemIcon>
            <ListItemText 
              primary={folder.name}
              secondary={`${folder.fileCount} 個檔案, ${folder.folderCount} 個資料夾`}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItemForMenu(folder);
                  setFolderMenuAnchor(e.currentTarget);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItemButton>
        </ListItem>
        {folder.children && folder.children.length > 0 && (
          renderFolderTree(folder.children, level + 1)
        )}
      </Box>
    ));
  };

  // 渲染檔案列表
  const renderFileList = () => {
    if (loadingContents) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            載入檔案列表中...
          </Typography>
        </Box>
      );
    }

    if (folderContents.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            此資料夾暫無檔案
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {folderContents.map((file) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            >
              <CardMedia
                component="div"
                sx={{
                  height: 140,
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                {file.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={file.thumbnailUrl}
                    alt={file.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    {getFileIcon(file.fileType)}
                    <Typography variant="caption" display="block">
                      {file.fileType.toUpperCase()}
                    </Typography>
                  </Box>
                )}
                
                {/* 檔案類型標籤 */}
                <Chip
                  label={file.fileType.toUpperCase()}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
              </CardMedia>
              
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                {/* 檔名欄位 - 顯示原始檔名.副檔名 */}
                <Typography 
                  variant="body2" 
                  component="h3" 
                  noWrap
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                  onClick={() => onFileSelect?.(file)}
                >
                  {file.originalName || file.name}
                </Typography>
                
                {/* 檔案信息 - 大小和時間 */}
                <Typography variant="caption" color="text.secondary" display="block">
                  {file.sizeFormatted} • {file.timeCreatedFormatted}
                </Typography>
                
                {file.tags && file.tags.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <UnifiedTagDisplay
                      tags={file.tags}
                      userTags={file.tags}
                      maxRows={3}
                      maxTagsPerRow={2}
                      readOnly={true}
                      variant="minimal"
                    />
                  </Box>
                )}
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Tooltip title="播放">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onFileSelect?.(file)}
                    >
                      <PlayIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="下載">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(file.url, '_blank')}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="移動到其他資料夾">
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setSelectedFileForMove(file);
                        setMoveFileDialog(true);
                      }}
                    >
                      <UploadIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setSelectedItemForMenu(file);
                      setFileMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 頂部操作欄 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            檔案管理
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CreateFolderIcon />}
              onClick={() => setCreateFolderDialog(true)}
              size="small"
            >
              新建資料夾
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchFolders();
                if (currentFolder) {
                  fetchFolderContents(currentFolder.id);
                } else {
                  // 🔧 新增：如果當前在根目錄，也刷新根目錄內容
                  fetchFolderContents('root');
                }
              }}
              size="small"
            >
              刷新
            </Button>
          </Box>
        </Box>

        {/* 麵包屑導航 */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 1 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={goToRoot}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            根目錄
          </Link>
          
          {breadcrumbPath.map((folder, index) => (
            <Link
              key={folder.id}
              component="button"
              variant="body2"
              onClick={() => enterFolder(folder)}
              sx={{ 
                textDecoration: 'none',
                color: index === breadcrumbPath.length - 1 ? 'text.primary' : 'primary.main',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {folder.name}
            </Link>
          ))}
        </Breadcrumbs>

        {/* 當前位置 */}
        {currentFolder && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon color="primary" />
            <Typography variant="body2" color="primary">
              當前位置: {currentFolder.name}
            </Typography>
            {currentFolder.description && (
              <Typography variant="caption" color="text.secondary">
                ({currentFolder.description})
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 主要內容區域 */}
      <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
        {/* 左側資料夾樹 */}
        <Paper elevation={1} sx={{ width: 300, p: 2, overflow: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            資料夾結構
          </Typography>
          
          {loadingFolders ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <LinearProgress />
              <Typography variant="caption">載入中...</Typography>
            </Box>
          ) : (
            <List dense>
              {renderFolderTree(folderTree)}
            </List>
          )}
        </Paper>

        {/* 右側檔案列表 */}
        <Paper elevation={1} sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {currentFolder ? `${currentFolder.name} 的內容` : '根目錄內容'}
            </Typography>
            
            {currentFolder && (
              <Button
                variant="outlined"
                size="small"
                onClick={goToParent}
              >
                返回上級
              </Button>
            )}
          </Box>

          {renderFileList()}
        </Paper>
      </Box>

      {/* 創建資料夾對話框 */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>創建新資料夾</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="資料夾名稱"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="描述 (可選)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newFolderDescription}
            onChange={(e) => setNewFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>取消</Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={!newFolderName.trim()}>
            創建
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編輯資料夾對話框 */}
      <Dialog open={editFolderDialog} onClose={() => setEditFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>編輯資料夾</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="資料夾名稱"
            fullWidth
            variant="outlined"
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="描述 (可選)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editFolderDescription}
            onChange={(e) => setEditFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFolderDialog(false)}>取消</Button>
          <Button onClick={handleRenameFolder} variant="contained" disabled={!editFolderName.trim()}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除資料夾對話框 */}
      <Dialog open={deleteFolderDialog} onClose={() => setDeleteFolderDialog(false)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除資料夾 "{selectedFolderForDelete?.name}" 嗎？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作無法撤銷，資料夾內的所有檔案將被移動到根目錄。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog(false)}>取消</Button>
          <Button onClick={handleDeleteFolder} variant="contained" color="error">
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 移動檔案對話框 */}
      <Dialog open={moveFileDialog} onClose={() => setMoveFileDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>移動檔案</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            將 "{selectedFileForMove?.originalName || selectedFileForMove?.name}" 移動到：
          </Typography>
          
          <TextField
            select
            label="目標資料夾"
            fullWidth
            variant="outlined"
            value={targetFolderId}
            onChange={(e) => setTargetFolderId(e.target.value)}
          >
            <MenuItem value="root">根目錄</MenuItem>
            {folders.map(folder => (
              <MenuItem key={folder.id} value={folder.id}>
                {folder.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveFileDialog(false)}>取消</Button>
          <Button onClick={handleMoveFile} variant="contained">
            移動
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔧 新增：刪除檔案對話框 */}
      <Dialog open={deleteFileDialog} onClose={() => setDeleteFileDialog(false)}>
        <DialogTitle>確認刪除檔案</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除檔案 "{selectedFileForDelete?.originalName || selectedFileForDelete?.name}" 嗎？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作無法撤銷，檔案將從系統中永久刪除。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFileDialog(false)}>取消</Button>
          <Button onClick={handleDeleteFile} variant="contained" color="error">
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 資料夾操作菜單 */}
      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={() => setFolderMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (selectedItemForMenu && 'type' in selectedItemForMenu) {
            const folder = selectedItemForMenu as Folder;
            setSelectedFolderForEdit(folder);
            setEditFolderName(folder.name);
            setEditFolderDescription(folder.description || '');
            setEditFolderDialog(true);
          }
          setFolderMenuAnchor(null);
        }}>
          <EditIcon sx={{ mr: 1 }} />
          重命名
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedItemForMenu && 'type' in selectedItemForMenu) {
            setSelectedFolderForDelete(selectedItemForMenu as Folder);
            setDeleteFolderDialog(true);
          }
          setFolderMenuAnchor(null);
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          刪除
        </MenuItem>
      </Menu>

      {/* 檔案操作菜單 */}
      <Menu
        anchorEl={fileMenuAnchor}
        open={Boolean(fileMenuAnchor)}
        onClose={() => setFileMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (selectedItemForMenu && !('type' in selectedItemForMenu)) {
            onFileSelect?.(selectedItemForMenu as FileInfo);
          }
          setFileMenuAnchor(null);
        }}>
          <PlayIcon sx={{ mr: 1 }} />
          播放
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedItemForMenu && !('type' in selectedItemForMenu)) {
            const file = selectedItemForMenu as FileInfo;
            window.open(file.url, '_blank');
          }
          setFileMenuAnchor(null);
        }}>
          <DownloadIcon sx={{ mr: 1 }} />
          下載
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedItemForMenu && !('type' in selectedItemForMenu)) {
            setSelectedFileForMove(selectedItemForMenu as FileInfo);
            setMoveFileDialog(true);
          }
          setFileMenuAnchor(null);
        }}>
          <UploadIcon sx={{ mr: 1 }} />
          移動到其他資料夾
        </MenuItem>
        {/* 🔧 新增：刪除檔案選項 */}
        <MenuItem onClick={() => {
          if (selectedItemForMenu && !('type' in selectedItemForMenu)) {
            setSelectedFileForDelete(selectedItemForMenu as FileInfo);
            setDeleteFileDialog(true);
          }
          setFileMenuAnchor(null);
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          刪除
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FolderManager;
